import { AppAssistRetry } from "./AppAssistRetry";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppExportRules } from "./AppExportRules";
import { AppRow } from "./AppIndex";
import { AppTaskAdd, AppTaskAnswer, AppTaskApi } from "./AppTaskApi";
import { AppTaskRules } from "./AppTaskRules";

// AppTaskActions - THE ONE SENDER of task changes (`POST /tasks`). The Tasks tab, the Today strip
// and the contact page's task strip all change a task through here and nowhere else, so they share
// one set of rules: the same body, the same reading of the answer, and ONE REQUEST IN FLIGHT PER
// TASK ID - a second tick on a task whose first change has not landed is refused with a sentence,
// because two changes racing on one row can land in either order.
//
// ⚠ THE WIRE IS `AppTaskApi` (the service agent's contract file, pinned by `TaskContract.test.ts`):
// its `Body` and `Parse` are used as they are, never re-implemented here.
// ⚠ NO TRANSPORT HERE: `IAppTaskHost.Post` is `AppMain`'s (an `AppAccountPost` to the service), so
// the suite drives this with a fake host and no browser.
// ⚠ A REFUSAL IS A SENTENCE (law 1: validation) handed to the caller to show in ink - never the
// error handler. A lost connection is a calm sentence; nothing was changed.

export type AppTaskDone = (Status: number, Text: string) => void;

/** What the sender needs from the page: the archive proof `/send` uses, and a POST. */
export interface IAppTaskHost {
    Phone(): string;
    Key(): string;
    /** POST the body to `/tasks`; `Done` answered exactly once - (0, "") for a lost connection. */
    Post(Body: string, Done: AppTaskDone): void;
    /**
     * ⭐ SEVERAL TABS (2026-09-27): the service CONFIRMED a change to a task on the page `Page` - the page
     * tells its other open tabs (`AppTabBus`). ⛔ A notice only; optional so a bare host still fits.
     */
    Confirmed?(Page: string, Task: AppRow): void;
    /** ⭐ Run `Then` once after `Ms` - a `retry` refusal (the archive still being restored) is sent again. Optional. */
    Later?(Ms: number, Then: () => void): void;
}

/** Hears how ONE change went, exactly once. */
export interface IAppTaskHeard {
    /**
     * Landed. `Task` is the service's row (null when it sent none - keep the optimistic one). `Changed`
     * false = it was already in that state (another tab) - no "Let <name> know?" card; absent = true.
     */
    Done(Task: AppRow | null, Changed?: boolean): void;
    /** Refused, lost or unreadable: put the row back and show `Why` as-is. */
    Failed(Why: string): void;
}

/**
 * Hears a change the service CONFIRMED (`/tasks` answered ok) - the Tasks tab, the Today strip and the
 * contact page all tell it. ⭐ `Closed` is what offers the "Let <name> know?" card (`AppTaskNotifier`);
 * ⛔ it is told only on a confirmed `done`, never on the optimistic tick, and it sends nothing.
 */
export interface IAppTaskDecided {
    Closed(Row: AppRow): void;
    Reopened(Id: string): void;
}

/**
 * Tell `Decided` about a confirmed change: `done` -> `Closed`, `reopen` -> `Reopened`; else nothing.
 * One place for the board and the frame, so both mean the same by "confirmed".
 * ⛔ `Changed` false (the service found it already done - a stale tab re-ticking what another tab closed)
 * offers NO card: the tab that really closed it has offered one already.
 */
export class AppTaskDecidedTell {
    public static Tell = (Decided: IAppTaskDecided | null, Row: AppRow | null, Action: string, Changed: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Decided === null || Row === null) { return; }
            if (Action === "done" && Changed === false) { return; }
            if (Action === "done") { (Decided as IAppTaskDecided).Closed(Row as AppRow); return; }
            if (Action === "reopen") { (Decided as IAppTaskDecided).Reopened((Row as AppRow).Id); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskDecidedTell.Tell");
        }
    };
}

export class AppTaskActions {
    private Host: IAppTaskHost;
    private InFlight: Record<string, boolean> = {};

    public constructor(Host: IAppTaskHost) {
        this.Host = Host;
    }

    /**
     * Change one task. "" when the request went (`Heard` then hears once, later); otherwise the
     * sentence to show, and nothing was sent. ⚠ `add` has no id yet; one add at a time per page.
     */
    public Send = (Page: string, Action: string, Id: string, Until: string, Add: AppTaskAdd | null,
                   Heard: IAppTaskHeard): string => {
        if (getCoreUtils().KErrorMode === true) { return AppTaskRules.LostWhy(); }
        try {
            const Slot: string = AppTaskActions.SlotOf(Action, Id);
            if (this.InFlight[Slot] === true) { return AppTaskRules.BusyWhy(); }
            const Body: string = AppTaskApi.Body(this.Host.Phone(), this.Host.Key(), Page, Action, Id, Until, Add);
            if (Body.length === 0) { return AppTaskActions.WhyNotSent(this.Host.Phone(), this.Host.Key(), Action); }
            this.InFlight[Slot] = true;
            this.Host.Post(Body, new AppTaskCall(this, Slot, Heard, Page, this.Host, Body).OnDone);
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskActions.Send");
            return AppTaskRules.LostWhy();
        }
    };

    /** Is a change to this task on its way? */
    public Busy = (Action: string, Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return this.InFlight[AppTaskActions.SlotOf(Action, Id)] === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskActions.Busy");
            return false;
        }
    };

    /** The call ended (either way): the slot is free again. */
    public End = (Slot: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            delete this.InFlight[Slot];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskActions.End");
        }
    };

    /** DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static SlotOf = (Action: string, Id: string): string => {
        if (Action === "add") { return "add"; }
        return "id:" + nz(Id);
    };

    /** Why a body could not be built: signed out, or a request the page should never have made. */
    private static WhyNotSent = (Phone: string, Key: string, Action: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(Phone).trim() === "" || nz(Key).trim() === "") { return AppTaskRules.SignInWhy(); }
            if (Action === "add") { return "Give the task a name and pick who it is with."; }
            if (Action === "snooze") { return "Pick a day to snooze it until."; }
            return "This task can't be changed from here.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskActions.WhyNotSent");
            return "";
        }
    };
}

/** One change in flight: frees its slot, reads the answer, tells its listener once. */
export class AppTaskCall {
    private Owner: AppTaskActions;
    private Slot: string;
    private Heard: IAppTaskHeard;
    private Retry: AppAssistRetry = new AppAssistRetry();
    private Page: string;
    private Host: IAppTaskHost | null;
    /** The body posted - sent again, unchanged, after a `retry` refusal. */
    private Body: string;
    /** Quiet re-sends after `retry` refusals (15 s each at the service = 5 minutes); then the sentence, as before. */
    public static readonly MaxAgain: number = AppExportRules.MaxAgain;

    public constructor(Owner: AppTaskActions, Slot: string, Heard: IAppTaskHeard, Page: string = "",
                       Host: IAppTaskHost | null = null, Body: string = "") {
        this.Owner = Owner;
        this.Slot = Slot;
        this.Heard = Heard;
        this.Page = nz(Page);
        this.Host = Host;
        this.Body = nz(Body);
    }

    /** ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - this is what releases the row's lock. */
    public OnDone = (Status: number, Text: string): void => {
        try {
            if (this.Retry.IsEnded() === true) { return; }
            const Answer: AppTaskAnswer = AppTaskApi.Parse(Status, Text, AppTaskRules.LostWhy());
            // ⭐ THE RESTART WINDOW (2026-09-29): the row stays as clicked and in flight, and the same change is sent
            // again - never "archive not built" put back on screen.
            if (this.Again(Answer) === true) { return; }
            this.Retry.End();
            this.Owner.End(this.Slot);
            if (Answer.Ok === true && Answer.Task !== null) {
                const Row: AppRow = AppTaskRules.RowFrom(Answer.Task);
                this.Heard.Done(Row, Answer.Changed);
                this.TellOthers(Row);
                return;
            }
            this.Heard.Failed(Answer.Why);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskCall.OnDone");
        }
    };

    /** Whether `Answer` is a `retry` refusal this call will send again (scheduled now). */
    private Again = (Answer: AppTaskAnswer): boolean => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - false lets `OnDone` release the row's lock.
        try {
            if (this.Host === null || this.Body.length === 0) { return false; }
            const Host: IAppTaskHost = this.Host as IAppTaskHost;
            if (Host.Later === undefined) { return false; }
            const Wait: number = this.Retry.WaitFor(Answer);
            if (Wait <= 0) { return false; }
            this.Retry.Schedule(Wait, Host, this.Resend);
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskCall.Again");
            return false;
        }
    };

    /** The same change, posted again; the answer comes back to `OnDone`. */
    public Resend = (): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - a lock is held until an answer comes; the catch reports.
        try {
            (this.Host as IAppTaskHost).Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskCall.Resend");
            this.OnDone(0, "");
        }
    };

    /** The other open tabs hear the confirmed row (`IAppTaskHost.Confirmed`) - a notice, never an action. */
    private TellOthers = (Row: AppRow): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Host === null) { return; }
            const Host: IAppTaskHost = this.Host as IAppTaskHost;
            if (Host.Confirmed !== undefined) { Host.Confirmed(this.Page, Row); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskCall.TellOthers");
        }
    };
}
